import { env } from '@/common/config';

/** Format money for quotation tables/drawers. */
export function formatQuoteMoney(amount, currency = 'INR') {
  return `${currency} ${parseFloat(amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Short date for customer quotation list. */
export function formatQuoteDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Formatted date-time for quotation details. */
export function formatQuoteDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/** Absolute URL for relative media / pdf_url paths. */
export function resolveQuotationMediaUrl(url) {
  if (!url) return '';
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('blob:')
  ) {
    return url;
  }
  const base = env.photoBaseUrl || env.apiBaseUrl || '';
  if (!base) return url;
  return `${base.replace(/\/$/, '')}/${String(url).replace(/^\//, '')}`;
}

/** Safe download filename for catalog PDF. */
export function quotationPdfFilename(quotation) {
  const raw =
    quotation?.quote_code || quotation?.title || `quotation-${quotation?.id}`;
  const safe = String(raw)
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
  return `${safe || 'quotation'}.pdf`;
}

/** Customer-facing WhatsApp numbers: sort by order, then id. */
export function sortQuotationProducts(products = []) {
  return [...products].sort((a, b) => {
    const orderA = a?.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b?.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return (a?.id ?? 0) - (b?.id ?? 0);
  });
}

/** Normalize tax rate input to "0.00"–"100.00" string, or null if invalid. */
export function normalizeTaxRate(value) {
  if (value === '' || value == null) return '0.00';
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return null;
  return Math.min(100, n).toFixed(2);
}

/** Line tax: (unit_price × qty) × tax_rate / 100 */
export function calcLineTaxAmount(unitPrice, quantity, taxRate) {
  const line = (parseFloat(unitPrice) || 0) * (parseInt(quantity, 10) || 0);
  return line * ((parseFloat(taxRate) || 0) / 100);
}

/** Hide delivery row when term blank and charge is 0. */
export function showDeliveryLine(term, charge) {
  return String(term || '').trim() !== '' || (parseFloat(charge) || 0) > 0;
}

/** Digits and one decimal only. Blocks letters while typing. */
export function sanitizeDeliveryChargeInput(value) {
  const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return cleaned;
  return `${cleaned.slice(0, dot + 1)}${cleaned.slice(dot + 1).replace(/\./g, '')}`;
}

export function normalizeDeliveryCharge(value) {
  if (value === '' || value == null) return '0.00';
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return null;
  return n.toFixed(2);
}
